'use client';

import {
  canReachAppOrigin,
  getBrowserOnlineStatus,
} from '@/lib/network-status';
import { useEffect, useRef, useState } from 'react';

const NETWORK_STATUS_RECHECK_INTERVAL_MS = 15_000;

export function useNetworkStatus(): boolean {
  const [isOnline, setIsOnline] = useState(true);
  const checkIdRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    let isMounted = true;

    const updateStatus = () => {
      if (getBrowserOnlineStatus()) {
        checkIdRef.current += 1;
        setIsOnline(true);
        return;
      }

      const checkId = checkIdRef.current + 1;
      checkIdRef.current = checkId;

      void canReachAppOrigin().then((canReachOrigin) => {
        if (!isMounted || checkId !== checkIdRef.current) {
          return;
        }

        setIsOnline(canReachOrigin);
      });
    };

    const updateStatusWhenVisible = () => {
      if (document.visibilityState !== 'hidden') {
        updateStatus();
      }
    };

    updateStatus();

    window.addEventListener('online', updateStatus);
    window.addEventListener('offline', updateStatus);
    window.addEventListener('focus', updateStatus);
    document.addEventListener('visibilitychange', updateStatusWhenVisible);

    const intervalId = window.setInterval(
      updateStatus,
      NETWORK_STATUS_RECHECK_INTERVAL_MS
    );

    return () => {
      isMounted = false;
      checkIdRef.current += 1;
      window.removeEventListener('online', updateStatus);
      window.removeEventListener('offline', updateStatus);
      window.removeEventListener('focus', updateStatus);
      document.removeEventListener('visibilitychange', updateStatusWhenVisible);
      window.clearInterval(intervalId);
    };
  }, []);

  return isOnline;
}

export function useReloadWhenBackOnline(enabled = true): void {
  const isOnline = useNetworkStatus();
  const shouldReloadOnReconnectRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (!isOnline) {
      shouldReloadOnReconnectRef.current = true;
      return;
    }

    if (shouldReloadOnReconnectRef.current) {
      shouldReloadOnReconnectRef.current = false;
      window.location.reload();
    }
  }, [enabled, isOnline]);
}
